import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import AddIcon from "@material-ui/icons/Add";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import PeopleIcon from "@material-ui/icons/People";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    textAlign: "center",
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: "bold",
  },
  metricLabel: {
    fontSize: "0.75rem",
    color: "#666",
    textTransform: "uppercase",
  },
  assemblyCard: {
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    borderLeft: "4px solid",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  tableHeader: {
    backgroundColor: "#1a5c1a",
    "& th": { color: "#fff", fontWeight: 600 },
  },
}));

const Assemblies = () => {
  const classes = useStyles();
  const [assemblies, setAssemblies] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [newAssembly, setNewAssembly] = useState({
    administradora: "",
    grupo: "",
    data: "",
    hora: "",
    local: "",
    tipo: "ordinaria",
    observacoes: "",
  });

  useEffect(() => {
    loadAssemblies();
  }, []);

  const loadAssemblies = async () => {
    try {
      const { data } = await api.get("/assemblies");
      setAssemblies(data);
    } catch (err) {
      // Dados de exemplo
      setAssemblies([]);
    }
  };

  const handleCreate = async () => {
    const assembly = {
      ...newAssembly,
      id: Date.now(),
      participantes: 0,
      notificados: 0,
      status: "agendada",
    };
    setAssemblies([...assemblies, assembly]);
    setOpenNew(false);
    setNewAssembly({ administradora: "", grupo: "", data: "", hora: "", local: "", tipo: "ordinaria", observacoes: "" });
    toast.success("Assembleia agendada com sucesso!");
  };

  const handleNotify = (assembly) => {
    toast.success(`Notificações enviadas para ${assembly.participantes} participantes do Grupo ${assembly.grupo}!`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "agendada": return "#2196f3";
      case "realizada": return "#4caf50";
      case "pendente": return "#ff9800";
      case "cancelada": return "#f44336";
      default: return "#9e9e9e";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const proximasAssembleias = assemblies.filter((a) => a.status === "agendada").length;
  const totalParticipantes = assemblies.reduce((s, a) => s + a.participantes, 0);
  const totalNotificados = assemblies.reduce((s, a) => s + a.notificados, 0);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Assembleias de Consórcio
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Agendamento e notificação de assembleias
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNew(true)}
          style={{ backgroundColor: "#2e7d32", color: "#fff" }}
        >
          Nova Assembleia
        </Button>
      </div>

      {/* Cards de Métricas */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.metricCard} style={{ borderTop: "4px solid #2196f3" }}>
            <EventIcon style={{ fontSize: 36, color: "#2196f3" }} />
            <Typography className={classes.metricValue} style={{ color: "#2196f3" }}>
              {proximasAssembleias}
            </Typography>
            <Typography className={classes.metricLabel}>Próximas Assembleias</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.metricCard} style={{ borderTop: "4px solid #4caf50" }}>
            <PeopleIcon style={{ fontSize: 36, color: "#4caf50" }} />
            <Typography className={classes.metricValue} style={{ color: "#4caf50" }}>
              {totalParticipantes}
            </Typography>
            <Typography className={classes.metricLabel}>Total Participantes</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.metricCard} style={{ borderTop: "4px solid #ff9800" }}>
            <NotificationsActiveIcon style={{ fontSize: 36, color: "#ff9800" }} />
            <Typography className={classes.metricValue} style={{ color: "#ff9800" }}>
              {totalNotificados}
            </Typography>
            <Typography className={classes.metricLabel}>Notificados via WhatsApp</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de Assembleias */}
      <TableContainer component={Paper} style={{ borderRadius: 12 }}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Administradora</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="center">Participantes</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assemblies.map((assembly) => (
              <TableRow key={assembly.id}>
                <TableCell style={{ fontWeight: 600 }}>{assembly.administradora}</TableCell>
                <TableCell>{assembly.grupo}</TableCell>
                <TableCell>
                  {formatDate(assembly.data)} às {assembly.hora}
                </TableCell>
                <TableCell>{assembly.local}</TableCell>
                <TableCell>
                  <Chip
                    label={assembly.tipo === "ordinaria" ? "Ordinária" : "Extraordinária"}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">{assembly.participantes}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={assembly.status.charAt(0).toUpperCase() + assembly.status.slice(1)}
                    size="small"
                    style={{
                      backgroundColor: `${getStatusColor(assembly.status)}22`,
                      color: getStatusColor(assembly.status),
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Notificar participantes via WhatsApp">
                    <IconButton size="small" onClick={() => handleNotify(assembly)} style={{ color: "#25d366" }}>
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" style={{ color: "#f44336" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Nova Assembleia */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ backgroundColor: "#2e7d32", color: "#fff" }}>
          Agendar Nova Assembleia
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Administradora"
                value={newAssembly.administradora}
                onChange={(e) => setNewAssembly({ ...newAssembly, administradora: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grupo"
                value={newAssembly.grupo}
                onChange={(e) => setNewAssembly({ ...newAssembly, grupo: e.target.value })}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data"
                type="date"
                value={newAssembly.data}
                onChange={(e) => setNewAssembly({ ...newAssembly, data: e.target.value })}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hora"
                type="time"
                value={newAssembly.hora}
                onChange={(e) => setNewAssembly({ ...newAssembly, hora: e.target.value })}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Local"
                value={newAssembly.local}
                onChange={(e) => setNewAssembly({ ...newAssembly, local: e.target.value })}
                variant="outlined"
                size="small"
                placeholder="Ex: Online - Zoom / Sede SP"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={newAssembly.tipo}
                onChange={(e) => setNewAssembly({ ...newAssembly, tipo: e.target.value })}
                variant="outlined"
                size="small"
              >
                <MenuItem value="ordinaria">Ordinária</MenuItem>
                <MenuItem value="extraordinaria">Extraordinária</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={newAssembly.observacoes}
                onChange={(e) => setNewAssembly({ ...newAssembly, observacoes: e.target.value })}
                variant="outlined"
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            style={{ backgroundColor: "#2e7d32", color: "#fff" }}
          >
            Agendar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Assemblies;
